import { Box, Stack, Typography } from "@mui/material"
import Icons from "@src/components/Icons"
import Logo from "@src/components/logo"
import StepDescriptionBox from "@src/components/StepDescriptionBox"
import { useTranslation } from "react-i18next"

const bulletListSx = {
  margin: 0,
  paddingLeft: "24px",
  fontSize: 16,
  fontWeight: "lighter",
  lineHeight: "25px",
  color: "#000",
  "& strong": {
    fontWeight: 700
  },
  "& ul": {
    margin: "8px 0 0",
    paddingLeft: "24px",
    listStyleType: "disc"
  }
}

const SectionHeader = ({
  icon,
  title,
  mb = 2
}: {
  icon: string
  title: string
  mb?: number
}) => (
  <Stack direction="row" alignItems="center" spacing={1} mb={mb}>
    <Icons
      justifyContent="center"
      name={icon}
      sx={{ transform: "scale(1)" }}
    />
    <Typography fontWeight={700} fontSize={22}>
      {title}
    </Typography>
  </Stack>
)

const HtmlListItem = ({ html }: { html: string }) => (
  <Box component="li" dangerouslySetInnerHTML={{ __html: html }} />
)

const AdditionalInformation = () => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        bgcolor: "#fff",
        borderRadius: 2,
        mt: 2
      }}
    >
      {/* Brand Introduction */}
      <Box>
        <Box sx={{ mb: 2 }}>
          <Logo />
        </Box>
        <Typography fontSize={16} fontWeight={400} lineHeight={1.7} mb={1.5}>
          {t("additional_info.brand.paragraph_1")}
        </Typography>
        <Typography fontSize={16} fontWeight={400} lineHeight={1.7}>
          {t("additional_info.brand.paragraph_2")}
        </Typography>
      </Box>

      {/* Size & Color Note Section */}
      <Box>
        <SectionHeader
          icon="note"
          title={t("additional_info.size_color_note.title")}
          mb={1}
        />
        <Box component="ul" sx={bulletListSx}>
          <li>{t("additional_info.size_color_note.size_guide")}</li>
          <li>{t("additional_info.size_color_note.color_variation")}</li>
        </Box>
      </Box>

      {/* Care Instructions Section */}
      <Box>
        <SectionHeader
          icon="care-instruction"
          title={t("additional_info.care_instructions.title")}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 1,
            justifyContent: "center"
          }}
        >
          <StepDescriptionBox iconName="care-instruction-step-1">
            {t("additional_info.care_instructions.hand_wash")}
          </StepDescriptionBox>
          <StepDescriptionBox iconName="care-instruction-step-2">
            {t("additional_info.care_instructions.use_laundry_bag")}
          </StepDescriptionBox>
          <StepDescriptionBox iconName="care-instruction-step-3">
            {t("additional_info.care_instructions.wash_separately")}
          </StepDescriptionBox>
          <StepDescriptionBox iconName="care-instruction-step-4">
            {t("additional_info.care_instructions.avoid_direct_sun")}
          </StepDescriptionBox>
          <StepDescriptionBox iconName="care-instruction-step-5">
            {t("additional_info.care_instructions.iron_carefully")}
          </StepDescriptionBox>
        </Box>
      </Box>

      {/* Purchase Policy Section */}
      <Box>
        <SectionHeader
          icon="purchase-policy"
          title={t("additional_info.purchase_policy.title")}
        />
        <Stack direction="row" spacing={3} justifyContent="center">
          <StepDescriptionBox iconName="purchase-policy-step-1">
            {t("additional_info.purchase_policy.record_unboxing")}
          </StepDescriptionBox>
          <StepDescriptionBox iconName="purchase-policy-step-2">
            {t("additional_info.purchase_policy.exchange_support")}
          </StepDescriptionBox>
          <StepDescriptionBox iconName="purchase-policy-step-3">
            {t("additional_info.purchase_policy.exchange_period")}
          </StepDescriptionBox>
          <StepDescriptionBox iconName="purchase-policy-step-4">
            {t("additional_info.purchase_policy.exchange_condition")}
          </StepDescriptionBox>
        </Stack>
      </Box>

      {/* Questions & Complaints */}
      <Box>
        <SectionHeader
          icon="complaints"
          title={t("additional_info.complaints.title")}
        />
        <Box component="ul" sx={bulletListSx}>
          <HtmlListItem html={t("additional_info.complaints.commitment")} />
          <li>{t("additional_info.complaints.unboxing_video")}</li>
          <li>{t("additional_info.complaints.inbox_shop")}</li>
        </Box>
      </Box>

      {/* Warranty & Returns */}
      <Box>
        <SectionHeader
          icon="warranty"
          title={t("additional_info.warranty.title")}
        />
        <Box component="ul" sx={bulletListSx}>
          <HtmlListItem html={t("additional_info.warranty.warranty_period")} />
          <HtmlListItem html={t("additional_info.warranty.exchange_period")} />
          <li>{t("additional_info.warranty.free_exchange")}</li>
          <Box component="li">
            <Box
              component="span"
              dangerouslySetInnerHTML={{
                __html: t("additional_info.warranty.exchange_fee_title")
              }}
            />
            <Box component="ul">
              <li>{t("additional_info.warranty.direct_exchange")}</li>
              <li>
                {t("additional_info.warranty.online_exchange")}
                <Box component="ul">
                  <li>{t("additional_info.warranty.online_fee_request")}</li>
                  <li>{t("additional_info.warranty.online_fee_defect")}</li>
                </Box>
              </li>
            </Box>
          </Box>
          <li>{t("additional_info.warranty.refund_policy")}</li>
        </Box>
      </Box>
    </Box>
  )
}

export default AdditionalInformation
